import { checkSecurityRulesAgainstObject } from '..';
import { UserGroupRoleType } from '../../types/UserGroupRole';
import { IntegrationProviderEnum } from 'constants-integrations';
import { PERMISSION_KEYS } from 'constants-permissions';
import {
  SecurityModesEnum,
  SecurityRuleTypeEnum,
  PropertySecurityRule,
} from '../../types/properties/PropertySecurity';
import {
  NestedPropertyRules,
  PropertyType,
} from 'lane-shared/types/properties/Property';
import getIntegrationDefinition, {
  getNestedIntegrationDefinition,
} from './getIntegrationDefinition';
import { get } from 'lodash';

type DefaultPolicy = 'deny' | 'allow';

/**
 * Rule applied to unmarked fields under a `defaultPolicy: 'deny'` integration:
 * the field is only returned to users who hold integration-read permission on
 * the channel. This prevents newly added credential fields from leaking simply
 * because nobody remembered to mark them `secure`.
 */
const DEFAULT_DENY_READ_RULES: PropertySecurityRule[] = [
  {
    type: SecurityRuleTypeEnum.ChannelPermission,
    value: PERMISSION_KEYS.PERMISSION_INTEGRATION_READ,
  },
];

type CheckIntegrationSecurityRulesProps = {
  // the channel integration object that we are securing
  readonly channelIntegration: any;
  // channel that this integration belongs to
  readonly channelId: string;
  // which action is being performed? read, update, etc?
  readonly action: SecurityModesEnum;
  // an array of group roles this user has, will check against the rules
  readonly userRoles: UserGroupRoleType[];
  // the id of the user making this secure request
  readonly userId: string;
};

type CheckIntegrationSecurityRulesOnNestedFieldsProps =
  CheckIntegrationSecurityRulesProps & {
    readonly nestedPropertyRules: NestedPropertyRules;
    readonly nestedSettings: any;
    // how to treat fields that carry no explicit `secure` marker
    readonly defaultPolicy: DefaultPolicy;
  };

/**
 * Decide whether a single settings field may be returned to the requesting
 * user. Precedence:
 *   1. `clientSafe` fields are always returned (needed by unprivileged clients).
 *   2. `secure` fields are gated by their own rules.
 *   3. Otherwise the integration's `defaultPolicy` decides: 'deny' requires
 *      integration-read permission, 'allow' returns the field.
 */
function isFieldReadable({
  fieldDefinition,
  defaultPolicy,
  action,
  userId,
  userRoles,
  channelId,
  ownerId,
}: {
  fieldDefinition?: PropertyType;
  defaultPolicy: DefaultPolicy;
  action: SecurityModesEnum;
  userId: string;
  userRoles: UserGroupRoleType[];
  channelId: string;
  ownerId: any;
}): boolean {
  if (fieldDefinition?.clientSafe) {
    return true;
  }

  let rules: PropertySecurityRule[];

  if (fieldDefinition?.secure) {
    rules = fieldDefinition.secure[action] ?? [];
  } else if (defaultPolicy === 'deny') {
    rules = DEFAULT_DENY_READ_RULES;
  } else {
    return true;
  }

  return checkSecurityRulesAgainstObject({
    permissions: [],
    user: { _id: userId, roles: userRoles },
    rules,
    sourceId: channelId,
    ownerId,
  });
}

function checkIntegrationSecurityRulesOnNestedFields({
  channelIntegration,
  userId,
  userRoles,
  channelId,
  action,
  nestedPropertyRules,
  nestedSettings,
  defaultPolicy,
}: CheckIntegrationSecurityRulesOnNestedFieldsProps) {
  const nestedFields: { [key: string]: any } = {};

  if (!nestedPropertyRules?.dynamicProviderKey) {
    return nestedFields;
  }

  // We will try to fetch the provider from the settings, if it's not there, we will use the key directly.
  const provider = get(
    channelIntegration.settings,
    nestedPropertyRules.dynamicProviderKey,
    nestedPropertyRules.dynamicProviderKey
  );

  if (!provider) {
    return nestedFields;
  }

  const nestedDefinitions = getNestedIntegrationDefinition(
    provider as IntegrationProviderEnum
  );

  if (Object.keys(nestedSettings).length === 0) {
    return nestedFields;
  }

  const ownerId =
    channelIntegration._createdBy?._id || channelIntegration._createdBy;

  Object.entries(nestedSettings).forEach(([key, value]) => {
    const isAllowed = isFieldReadable({
      fieldDefinition: nestedDefinitions[key],
      defaultPolicy,
      action,
      userId,
      userRoles,
      channelId,
      ownerId,
    });

    if (isAllowed) {
      nestedFields[key] = value;
    }
  });

  return nestedFields;
}

export default function checkIntegrationSecurityRules({
  channelIntegration,
  channelId,
  action = SecurityModesEnum.Read,
  userRoles,
  userId,
}: CheckIntegrationSecurityRulesProps): object {
  // Don't modify the original object, return a new one.
  const ret: { [key: string]: any } = {};

  if (!channelIntegration) {
    return ret;
  }

  const definition = getIntegrationDefinition(
    channelIntegration.integration.name as IntegrationProviderEnum
  );

  if (!definition?.properties) {
    return ret;
  }

  // The definition's policy only governs the provider-specific nested settings
  // blobs (where all credentials live). Top-level settings remain default-allow
  // so benign client fields not present in the definition (e.g. supportPage,
  // invitationBaseUrl) are still returned to unprivileged clients.
  const nestedDefaultPolicy: DefaultPolicy = definition.defaultPolicy ?? 'allow';
  const ownerId =
    channelIntegration._createdBy?._id || channelIntegration._createdBy;

  // Iterate over the integration properties definition.
  Object.entries(channelIntegration.settings).forEach(([key, value]) => {
    const fieldDefinition = definition.properties[key];

    if (fieldDefinition?.nestedPropertyRules?.checkSecurity) {
      ret[key] = checkIntegrationSecurityRulesOnNestedFields({
        channelIntegration,
        userId,
        userRoles,
        channelId,
        action,
        nestedPropertyRules: fieldDefinition.nestedPropertyRules,
        nestedSettings: value,
        defaultPolicy: nestedDefaultPolicy,
      });
    } else if (
      isFieldReadable({
        fieldDefinition,
        defaultPolicy: 'allow',
        action,
        userId,
        userRoles,
        channelId,
        ownerId,
      })
    ) {
      ret[key] = value;
    }
  });

  return ret;
}
